export default {
  name: `Minimum Job Requirements and/or Alternative Job Requirements`,
  prefix: "F.b/c",
  sections: [
    {
      name: "Minimum Job Requirements",
      sectionInfo: {
        text: `<b>Note:</b> This section must only include a single set of requirements, the employer’s minimum requirements for each question.  If the employer will accept alternative requirements, the alternative requirements must be entered in Section F.c.`,
        coloring: "grey",
      },
      fields: [
        {
          isGroup: {
            code: "F.b.1",
            machine_name: "primaryEducationLevel",
            required: true,
            label: "Education: Minimum U.S. degree required",
            tooltip: `Identify whether the minimum U.S. degree required by the employer for the job opportunity is None, High School/GED, Associates, Bachelor’s, Master’s, Doctorate, or Other degree. Only mark one box indicating the minimum education requirements for the job opportunity. Select the “Other degree” option if the job opportunity requires a post-bachelor’s degree that is not covered by the first six options, such as a J.D., M.D., or D.D.S. Do not select “Other degree” if the job opportunity requires certifications, licenses, diplomas, or other credentials obtained through a limited period of training or coursework, such as a certification that employers in a particular industry commonly require in addition to the formal educational degree requirements of the occupation. For example, do not check “Other degree” if the job opportunity requires a teaching certification obtained after completion of one year of training and coursework, a software development certificate obtained after completion of a six-month boot camp, or a license obtained through a period of apprenticeship, residency, or internship. These requirements should be listed in F.b.3 or F.b.5. as provided in the instructions below.`,
            coloring: "none",
            type: "radio",
            fields: [
              {
                widget: "radio",
                choice: "None",
                value: "NONE",
                validate: ["required"],
                clearSchema: ["otherEducation", "major"],
              },
              {
                widget: "radio",
                choice: "High School/GED",
                value: "HIGHSCHOOLGED",
                validate: ["required"],
                clearSchema: ["otherEducation", "major"],
              },
              {
                widget: "radio",
                choice: "Associate's",
                value: "ASSOCIATES",
                validate: ["required"],
                clearSchema: ["otherEducation"],
              },
              {
                widget: "radio",
                choice: "Bachelor's",
                value: "BACHELORS",
                validate: ["required"],
                clearSchema: ["otherEducation"],
              },
              {
                widget: "radio",
                choice: "Master's",
                value: "MASTERS",
                validate: ["required"],
                clearSchema: ["otherEducation"],
              },
              {
                widget: "radio",
                choice: "Doctorate (Ph.D.)",
                value: "DOCTORATEPHD",
                validate: ["required"],
                clearSchema: ["otherEducation"],
              },
              {
                widget: "radio",
                choice: "Other degree (J.D., M.D., etc.)",
                value: "OTHERDEGREE",
                validate: ["required"],
              },
            ],
          },
        },
        {
          code: "F.b.1.a",
          label:
            "If “Other degree” in question 1, specify the U.S. degree required (e.g., J.D., M.D., D.D.S., etc.). §",
          machine_name: "otherEducation", // need machine name here for group level error messaging
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "primaryEducationLevel",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "OTHERDEGREE",
              },
            },
          ],
          isVisible: {
            when: "primaryEducationLevel",
            is: "OTHERDEGREE",
          },
          tooltip: `If “Other degree” in question 1, enter the specific U.S. degree required. (e.g., J.D., M.D., D.D.S., etc.). The “Other degree” listed here must be offered by at least one U.S. institution accredited by an organization recognized by the U.S. Department of Education. Please do not list here additional supporting education like specific degree or course requirements, which should be included in response to question F.b.5 below.`,
        },
        {
          code: "F.b.1.b",
          label:
            "Indicate the major(s) and/or field(s) of study required § (May list more than one related major and more than one field)",
          machine_name: "major", // need machine name here for group level error messaging
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "primaryEducationLevel",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "ASSOCIATES",
                  "BACHELORS",
                  "MASTERS",
                  "DOCTORATEPHD",
                  "OTHERDEGREE",
                ],
              },
            },
          ],
          isVisible: {
            when: "primaryEducationLevel",
            is: [
              [
                "ASSOCIATES",
                "BACHELORS",
                "MASTERS",
                "DOCTORATEPHD",
                "OTHERDEGREE",
              ],
            ],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
          tooltip: `Enter the major(s) and/or field(s) of study required by the employer for the job opportunity. The major and field of study must be offered by at least one U.S. institution accredited by an organization recognized by the U.S. Department of Education. You may list more than one field and/or more than one related major. If the answer to question 1 is “None” or “High School,” leave blank or enter “N/A.”`,
        },
        {
          isGroup: {
            code: "F.b.2",
            machine_name: "secondDiploma",
            label: "Does the employer require a second U.S. degree?",
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                clearSchema: ["secondDiplomaMajor"],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                setSchema: [
                  {
                    set: "secondDiplomaMajor",
                    to: "N/A",
                  },
                ],
              },
            ],
            tooltip: `If the employer requires a second U.S. degree for the job opportunity, mark “Yes.” Otherwise, mark “No.”`,
          },
        },
        {
          code: "F.b.2.a",
          machine_name: "secondDiplomaMajor",
          label:
            "If “Yes” in question 2, indicate the second U.S. degree and the major(s) and/or field(s) of study required §",
          tooltip: `If “Yes” in question 2, enter the specific second U.S. degree required.`,
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "secondDiploma",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          disabled: true,
          disableWhenField: {
            when: "secondDiploma",
            is: 0,
          },
          isVisible: {
            when: "secondDiploma",
            is: 1,
          },
        },
        {
          isGroup: {
            code: "F.b.3",
            machine_name: "trainingRequired",
            label: "Is training for the job opportunity required?",
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                clearSchema: ["numberOfMonthsTraining", "nameRequiredTraining"],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                setSchema: [
                  {
                    set: "numberOfMonthsTraining",
                    to: null,
                  },
                  {
                    set: "nameRequiredTraining",
                    to: "N/A",
                  },
                ],
              },
            ],
            tooltip: `If the employer requires training for the job opportunity, mark “Yes.”  Otherwise, mark “No.”  Training may include, but is not limited to:  programs, coursework, or training experience (other than employment).  Do not include on-the-job training required by the employer after the date of hire.  When answering this question, do not duplicate requirements that are listed in other fields of this application; the training required must not be counted as education, work experience, or specific skills or other requirements.  
            <ul><li>a. If “Yes” in question 3, enter the number of months of training required by the employer for the job opportunity.  If the answer to question 3 is “No,” enter “0” (zero).  When answering this question, do not duplicate time requirements that are listed in other fields of this application; the training time required is in addition to the required education and experience, and must not be counted as (added to) education or experience time required. </li>
            <li>b. If “Yes” in question 3, enter the field(s) and/or name(s) of the training required by the employer for the job opportunity.  You may list more than one field and/or more than one name.  If the answer to question 3 is “No,” enter “N/A.”</li></ul>`,
          },
        },
        {
          code: "F.b.3.a",
          machine_name: "numberOfMonthsTraining",
          label: `If “Yes” in question 3, specify the number of months of training required §`,
          widget: "text",
          maxLength: 4,
          validate: [
            {
              isRequired: "trainingRequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          parse: "positiveWholeNumberToInt",
          disabled: true,
          disableWhenField: {
            when: "trainingRequired",
            is: 0,
          },
          isVisible: {
            when: "trainingRequired",
            is: 1,
          },
        },
        {
          code: "F.b.3.b",
          machine_name: "nameRequiredTraining",
          label: `Indicate the field(s)/name(s) of training required §(May list more than one related field and more than one type)`,
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "trainingRequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          disabled: true,
          disableWhenField: {
            when: "trainingRequired",
            is: 0,
          },
          isVisible: {
            when: "trainingRequired",
            is: 1,
          },
        },
        {
          isGroup: {
            code: "F.b.4",
            machine_name: "empExperienceRequired",
            label: "Is employment experience required?",
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
                clearSchema: ["empExperienceMonths"],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                clearSchema: ["occupationRequired"],
                setSchema: [
                  {
                    set: "empExperienceMonths",
                    to: null,
                  },
                ],
              },
            ],
            tooltip: `<ul>If the employer requires employment experience, mark “Yes.” Otherwise, mark “No.”
            <li>a. If “Yes” in question 4, enter the number of months of experience required by the employer. If the answer to question 4 is “No”, enter “0” (zero).</li>
            <li>b. If “Yes” in question 4, enter the occupation in which experience is required by the employer.</li></ul>`,
          },
        },
        {
          code: "F.b.4.a",
          machine_name: "empExperienceMonths",
          label: `If “Yes” in question 4, specify the number of months of experience required §`,
          widget: "text",
          maxLength: 4,
          validate: [
            {
              isRequired: "empExperienceRequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          parse: "positiveWholeNumberToInt",
          disabled: true,
          disableWhenField: {
            when: "empExperienceRequired",
            is: 0,
          },
          isVisible: {
            when: "empExperienceRequired",
            is: 1,
          },
        },
        {
          code: "F.b.4.b",
          machine_name: "occupationRequired",
          label: `Indicate the occupation required §`,
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "empExperienceRequired",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          disabled: true,
          disableWhenField: {
            when: "empExperienceRequired",
            is: 0,
          },
          isVisible: {
            when: "empExperienceRequired",
            is: 1,
          },
        },
        {
          isGroup: {
            code: "F.b.5",
            machine_name: "isSpecialRequirements",
            label:
              "Special skills or other requirements: Does the employer require any specific or other requirements?",
            tooltip: `If the employer requires specific skills or other requirements for the job opportunity, mark “Yes.” Otherwise, mark “No.”
            <ul><li>a. If “Yes” in question 5, mark all that apply (License/Certification, Foreign language, Residency/Fellowship, and/or Other skills or requirements). Next to each marked item, specify the name or type of requirement. Examples include additional supporting education, such as specific degrees or courses, specific foreign language proficiency, test results. When answering this question, do not duplicate requirements – the specific skills or other requirements must not be counted as education, training, or experience required. For Alternative Requirements, see Section F.c.</li></ul>`,
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: ["required"],
                clearSchema: [
                  "isSpecReqLicenseCert",
                  "isSpecReqForeignLang",
                  "isSpecReqResFellow",
                  "isSpecReqOther",
                  "specReqLicenseCert",
                  "specReqForeignLang",
                  "specReqResFellow",
                  "specReqOther",
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "F.b.5.a",
            label:
              "If “Yes,” check all that apply and specify the requirement(s): §",
            coloring: "none",
            className: "checkboxTextInputGroup",
            fieldLevelErrors: true,
            showIndividualFieldError: true,
            isVisible: {
              when: "isSpecialRequirements",
              is: 1,
            },
            fields: [
              {
                machine_name: "fb5Group",
                widget: "text",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecialRequirements",
                  is: 0,
                },
                validate: [
                  {
                    subsetOfSetRequired: "fb5Group",
                    message: "Please complete at least one.",
                    needsFieldValues: true,
                    extraData: {
                      when: "isSpecialRequirements",
                      is: "=",
                      to: 1,
                      numRequired: 1,
                      fieldArray: [
                        "specReqLicenseCert",
                        "specReqForeignLang",
                        "specReqResFellow",
                        "specReqOther",
                      ],
                    },
                  },
                ],
              },
              {
                machine_name: "isSpecReqLicenseCert",
                widget: "checkbox",
                choice: "(i) License/Certification",
                label: "(i) License/Certification",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecialRequirements",
                  is: 0,
                },
                clearSchema: ["specReqLicenseCert"],
              },
              {
                code: "F.b.5.a",
                label: "(i) License/Certification:",
                hidden: true,
                machine_name: "specReqLicenseCert",
                widget: "text_area",
                width: 8,
                rows: 6,
                inputLimit: 300,
                disabled: true,
                disableWhenField: {
                  when: "isSpecReqLicenseCert",
                  is: null,
                },
                isVisible: {
                  when: "isSpecReqLicenseCert",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isSpecReqLicenseCert",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isSpecReqForeignLang",
                widget: "checkbox",
                choice: "(ii) Foreign language",
                label: "(ii) Foreign language",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecialRequirements",
                  is: 0,
                },
                clearSchema: ["specReqForeignLang"],
              },
              {
                machine_name: "specReqForeignLang",
                widget: "text_area",
                code: "F.b.5.a",
                label: "(ii) Foreign Language:",
                hidden: true,
                width: 8,
                rows: 6,
                inputLimit: 300,
                // label: "List special requirements and license certifications here.",
                disabled: true,
                disableWhenField: {
                  when: "isSpecReqForeignLang",
                  is: null,
                },
                isVisible: {
                  when: "isSpecReqForeignLang",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isSpecReqForeignLang",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isSpecReqResFellow",
                widget: "checkbox",
                choice: "(iii) Residency/Fellowship",
                label: "(iii) Residency/Fellowship",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecialRequirements",
                  is: 0,
                },
                clearSchema: ["specReqResFellow"],
              },
              {
                machine_name: "specReqResFellow",
                widget: "text_area",
                width: 8,
                rows: 6,
                inputLimit: 300,
                code: "F.b.5.a",
                label: "(iii) Residency/Fellowship:",
                hidden: true,
                // label: "List special requirements and license certifications here.",
                disabled: true,
                disableWhenField: {
                  when: "isSpecReqResFellow",
                  is: null,
                },
                isVisible: {
                  when: "isSpecReqResFellow",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isSpecReqResFellow",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isSpecReqOther",
                widget: "checkbox",
                choice: "(iv) Other special skills or requirements",
                label: "(iv) Other special skills or requirements",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecialRequirements",
                  is: 0,
                },
                clearSchema: ["specReqOther"],
              },
              {
                machine_name: "specReqOther",
                widget: "text_area",
                width: 8,
                rows: 10,
                inputLimit: 4000,
                code: "F.b.5.a",
                label: "(iv) Other Special Skills or Requirements:",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isSpecReqOther",
                  is: null,
                },
                isVisible: {
                  when: "isSpecReqOther",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isSpecReqOther",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
    },
    {
      name: `Alternative Job Requirements`,
      sectionInfo: {
        text: `While an employer may specify alternative requirements, the substantial equivalency of the alternative requirements to minimum requirements will not be evaluated. If there are alternative sets of Education, Training, and/or Experience requirements, all fields in this section (Section F.c.) must be completed. <b> Note: </b> This section is not applicable for H-2B applications.`,
        coloring: "grey",
      },
      fields: [
        {
          isGroup: {
            code: "F.c.1",
            machine_name: "altJobRequirement",
            label:
              "Are alternate sets of education, training, and/or experience accepted?",
            tooltip: `Identify whether an alternative set of requirements is accepted for the job opportunity. If your answer is “Yes,” you must complete c.2, c.3., and c.4. Any additional education requirements must be entered with the “Special Skills or Other Requirements” (Section F.c.5)`,
            type: "radio",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: [
                  "required",
                  {
                    isRequired: "visaType",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: [
                        "H-1B",
                        "H-1B1 Chile",
                        "H-1B1 Singapore",
                        "E-3 Australian",
                        "PERM",
                      ],
                    },
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
                clearSchema: [
                  "altEducationLevel",
                  "altOtherEdu",
                  "altMajor",
                  "altTrainingReq",
                  "altNumMonthsTraining",
                  "altNameReqdTraining",
                  "altEmpExperienceReqd",
                  "altEmpExpNumMonths",
                  "isAltSpecialReq",
                  "isAltSpecReqLicenseCert",
                  "altSpecReqLicenseCert",
                  "isAltSpecReqForeignLang",
                  "altSpecReqForeignLang",
                  "isAltSpecReqResFellow",
                  "altSpecReqResFellow",
                  "isAltSpecReqOther",
                  "altSpecReqOther",
                ],
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: [
                  "required",
                  {
                    isRequired: "visaType",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: [
                        "H-1B",
                        "H-1B1 Chile",
                        "H-1B1 Singapore",
                        "E-3 Australian",
                        "PERM",
                      ],
                    },
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "visaType",
                  is: "H-2B",
                },
                clearSchema: [
                  "altEducationLevel",
                  "altOtherEdu",
                  "altMajor",
                  "altTrainingReq",
                  "altNumMonthsTraining",
                  "altNameReqdTraining",
                  "altEmpExperienceReqd",
                  "altEmpExpNumMonths",
                  "isAltSpecialReq",
                  "isAltSpecReqLicenseCert",
                  "altSpecReqLicenseCert",
                  "isAltSpecReqForeignLang",
                  "altSpecReqForeignLang",
                  "isAltSpecReqResFellow",
                  "altSpecReqResFellow",
                  "isAltSpecReqOther",
                  "altSpecReqOther",
                ],
              },
            ],
          },
        },
        {
          isGroup: {
            code: "F.c.2",
            machine_name: "altEducationLevel",
            label:
              "Specify the alternate level of education: U.S. degree accepted §",
            tooltip: ` Identify what alternative level of education is accepted.
            <ul><li>a. If “Other” in question 2, enter the specific U.S. diploma or degree accepted. (Example: J.D., M.D., D.D.S., etc.). If the answer to question 2 is not “Other,” enter “N/A.</li>
            <li>b. Enter the major(s) and/or field(s) of study accepted by the employer for the job opportunity. The major and field of study must be offered by an accredited U.S. institution. You may list more than one field and/or more than one related major. If the answer to question 2 is “None” or “High School,” enter “N/A.</li></ul>`,
            type: "radio",
            isVisible: {
              when: "altJobRequirement",
              is: 1,
            },
            fields: [
              {
                widget: "radio",
                choice: "None",
                value: "NONE",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altOtherEdu", "altMajor"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "High School/GED",
                value: "HIGHSCHOOLGED",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },

                clearSchema: ["altOtherEdu", "altMajor"],
              },
              {
                widget: "radio",
                choice: "Associate's",
                value: "ASSOCIATES",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altOtherEdu"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "Bachelor's",
                value: "BACHELORS",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altOtherEdu"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "Master's",
                value: "MASTERS",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altOtherEdu"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "Doctorate (Ph.D.)",
                value: "DOCTORATEPHD",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altOtherEdu"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "Other degree (JD, MD, etc.)",
                value: "OTHERDEGREE",
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
            ],
          },
        },
        {
          code: "F.c.2.a",
          label:
            "If “Other degree” in question 2, specify the U.S. degree accepted §",
          machine_name: "altOtherEdu",
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "altEducationLevel",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "OTHERDEGREE",
              },
            },
          ],
          isVisible: {
            when: "altEducationLevel",
            is: "OTHERDEGREE",
          },
          tooltip: `If “Other” in question 2, enter the specific U.S. diploma or degree accepted. (Example: J.D., M.D., D.D.S., etc.). If the answer to question 2 is not “Other,” enter “N/A.”`,
          disabled: true,
          disableWhenField: {
            when: "altJobRequirement",
            is: 0,
          },
        },
        {
          code: "F.c.2.b",
          label:
            "Indicate the major(s) and/or field(s) of study accepted § (May list more than one related major and more than one field)",
          machine_name: "altMajor", // need machine name here for group level error messaging
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          validate: [
            {
              isRequired: "altEducationLevel",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "ASSOCIATES",
                  "BACHELORS",
                  "MASTERS",
                  "DOCTORATEPHD",
                  "OTHERDEGREE",
                ],
              },
            },
          ],
          isVisible: {
            when: "altEducationLevel",
            is: [
              [
                "ASSOCIATES",
                "BACHELORS",
                "MASTERS",
                "DOCTORATEPHD",
                "OTHERDEGREE",
              ],
            ],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
          tooltip: `Enter the major(s) and/or field(s) of study accepted by the employer for the job opportunity. The major and field of study must be offered by a U.S. institution accredited by an organization recognized by the U.S. Department of Education. You may list more than one field and/or more than one related major.`,
          disabled: true,
          disableWhenField: {
            when: "altJobRequirement",
            is: 0,
          },
        },
        {
          isGroup: {
            code: "F.c.3",
            machine_name: "altTrainingReq",
            label: "Is alternate training for the job opportunity accepted? §",
            type: "radio",
            isVisible: {
              when: "altJobRequirement",
              is: 1,
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altNumMonthsTraining", "altNameReqdTraining"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                setSchema: [
                  {
                    set: "altNumMonthsTraining",
                    to: null,
                  },
                  {
                    set: "altNameReqdTraining",
                    to: "N/A",
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
            ],
            tooltip: `If the employer accepts alternative training for the job opportunity, mark “Yes.” Otherwise, mark “No.” 
            Training may include, but is not limited to: programs, coursework, or training experience (other than employment). 
            Do not include on-the-job training required by the employer after the date of hire. When answering this question, 
            do not duplicate requirements that are listed in other fields of this application; the training accepted must not be 
            counted as education or work experience entered in this section or specific skills or other requirements entered in 
            Section F.b.5. If the employer seeks prevailing wage determination with additional sets of alternative job requirements, 
            the employer must submit another Form ETA-9141.
            <ul><li>a. If “Yes” in question 3, enter the number of months of training accepted by the employer for the job opportunity. 
            If the answer to question 2 is “No,” enter “0” (zero). When answering this question, do not duplicate time requirements – 
            the training time accepted is in addition to the required education and experience and must not be counted as (added to) 
            education or experience time accepted.</li>
            <li>b. If “Yes” in question 3, enter the field(s) and/or name(s) of the training accepted by the employer for the job 
            opportunity. You may list more than one field and/or more than one name. If the answer to question 2 is “No,” enter “N/A.”</li></ul>`,
          },
        },
        {
          code: "F.c.3.a",
          machine_name: "altNumMonthsTraining",
          label: `If “Yes” in question 3, specify the number of months of alternate training accepted §`,
          widget: "text",
          maxLength: 4,
          validate: [
            {
              isRequired: "altTrainingReq",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          parse: "positiveWholeNumberToInt",
          disabled: true,
          disableWhenField: {
            when: "altTrainingReq",
            is: 0,
          },
          isVisible: {
            when: "altJobRequirement altTrainingReq",
            is: [[1], [1]],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
        },
        {
          code: "F.c.3.b",
          machine_name: "altNameReqdTraining",
          label: `Indicate the field(s)/name(s) of training accepted §(May list more than one related field and more than one type)`,
          widget: "text_area",
          width: 8,
          rows: 4,
          inputLimit: 200,
          isVisible: {
            when: "altJobRequirement altTrainingReq",
            is: [[1], [1]],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
          validate: [
            {
              isRequired: "altTrainingReq",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          disabled: true,
          disableWhenField: {
            when: "altTrainingReq",
            is: 0,
          },
        },
        {
          isGroup: {
            code: "F.c.4",
            machine_name: "altEmpExperienceReqd",
            label: "Is alternate employment experience accepted? §",
            type: "radio",
            isVisible: {
              when: "altJobRequirement",
              is: 1,
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: ["altEmpExpNumMonths"],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                setSchema: [
                  {
                    set: "altEmpExpNumMonths",
                    to: null,
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
            ],
            tooltip: `<ul>If the employer accepts alternative employment experience, mark “Yes.” Otherwise, mark “No.”
            <li>a. If “Yes” in question 4, enter the number of months of experience accepted by the employer. If the answer to question 4 is “No,” enter “0” (zero).</li></ul>`,
          },
        },
        {
          code: "F.c.4.a",
          machine_name: "altEmpExpNumMonths",
          label: `If “Yes” in question 4, specify the number of months of alternate experience accepted §`,
          widget: "text",
          maxLength: 4,
          validate: [
            {
              isRequired: "altEmpExperienceReqd",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: 1,
              },
            },
          ],
          parse: "positiveWholeNumberToInt",
          disabled: true,
          disableWhenField: {
            when: "altEmpExperienceReqd",
            is: 0,
          },
          isVisible: {
            when: "altJobRequirement altEmpExperienceReqd",
            is: [[1], [1]],
            operator: "compareMultiFieldValuesAND_ORInArray",
          },
        },
        {
          isGroup: {
            code: "F.c.5",
            machine_name: "isAltSpecialReq",
            label:
              "Special Skills or Other Requirements: Does the employer require any specific or other requirements?",
            tooltip: `If the employer requires specific skills or other requirements for the job opportunity, mark “Yes.” Otherwise, mark “No.”
            <ul><li>a. If “Yes” in question 5, mark all that apply (License/Certification, Foreign Language, Residency/Fellowship, and/or Other Skills or Requirements). Next to each marked item, specify the name or type of requirement. Examples include additional supporting education, such as specific degrees or courses, specific foreign language proficiency, test results. When answering this question, do not duplicate requirements – the specific skills or other requirements must not be counted as education, training, or experience required.</li></ul>`,
            type: "radio",
            isVisible: {
              when: "altJobRequirement",
              is: 1,
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: 1,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
              {
                widget: "radio",
                choice: "No",
                value: 0,
                validate: [
                  {
                    isRequired: "altJobRequirement",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
                clearSchema: [
                  "isAltSpecReqLicenseCert",
                  "isAltSpecReqForeignLang",
                  "isAltSpecReqResFellow",
                  "isAltSpecReqOther",
                  "altSpecReqLicenseCert",
                  "altSpecReqForeignLang",
                  "altSpecReqResFellow",
                  "altSpecReqOther",
                ],
                disabled: true,
                disableWhenField: {
                  when: "altJobRequirement",
                  is: 0,
                },
              },
            ],
          },
        },
        {
          isGroup: {
            code: "F.c.5.a",
            label:
              "If “Yes,” check all that apply and specify the requirement(s): §",
            coloring: "none",
            className: "checkboxTextInputGroup",
            fieldLevelErrors: true,
            showIndividualFieldError: true,
            // isAltSpecReqLicenseCert: Number,
            // altSpecReqLicenseCert: String,
            // isAltSpecReqForeignLang: Number,
            // altSpecReqForeignLang: String,
            // isAltSpecReqResFellow: Number,
            // altSpecReqResFellow: String,
            // isAltSpecReqOther: Number,
            // altSpecReqOther: String,
            machine_name: "isAltSpecReqLicenseCert",
            isVisible: {
              when: "altJobRequirement isAltSpecialReq",
              is: [[1], [1]],
              operator: "compareMultiFieldValuesAND_ORInArray",
            },
            fields: [
              {
                machine_name: "fc5Group",
                widget: "text",
                hidden: true,
                disabled: true,
                validate: [
                  {
                    subsetOfSetRequired: "fc5Group",
                    message: "Please complete at least one.",
                    needsFieldValues: true,
                    extraData: {
                      when: "isAltSpecialReq",
                      is: "=",
                      to: 1,
                      numRequired: 1,
                      fieldArray: [
                        "altSpecReqLicenseCert",
                        "altSpecReqForeignLang",
                        "altSpecReqResFellow",
                        "altSpecReqOther",
                      ],
                    },
                  },
                ],
              },
              {
                machine_name: "isAltSpecReqLicenseCert", // use validation check for group level validation
                widget: "checkbox",
                choice: "(i) License/Certification",
                label: "(i) License/Certification",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecialReq",
                  is: [0, null],
                },
                clearSchema: ["altSpecReqLicenseCert"],
              },
              {
                code: "F.c.5.a",
                label: "(i) License/Certification:",
                hidden: true,
                machine_name: "altSpecReqLicenseCert",
                widget: "text_area",
                width: 8,
                rows: 6,
                inputLimit: 300,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecReqLicenseCert",
                  is: null,
                },
                isVisible: {
                  when: "isAltSpecReqLicenseCert",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isAltSpecReqLicenseCert",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isAltSpecReqForeignLang",
                widget: "checkbox",
                choice: "(ii) Foreign Language",
                label: "(ii) Foreign Language",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecialReq",
                  is: [0, null],
                },
                clearSchema: ["altSpecReqForeignLang"],
              },
              {
                machine_name: "altSpecReqForeignLang",
                widget: "text_area",
                code: "F.c.5.a",
                label: "(ii) Foreign Language:",
                hidden: true,
                width: 8,
                rows: 6,
                inputLimit: 300,
                // label: "List special requirements and license certifications here.",
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecReqForeignLang",
                  is: null,
                },
                isVisible: {
                  when: "isAltSpecReqForeignLang",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isAltSpecReqForeignLang",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isAltSpecReqResFellow",
                widget: "checkbox",
                choice: "(iii) Residency/Fellowship",
                label: "(iii) Residency/Fellowship",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecialReq",
                  is: [0, null],
                },
                clearSchema: ["altSpecReqResFellow"],
              },
              {
                machine_name: "altSpecReqResFellow",
                widget: "text_area",
                width: 8,
                rows: 6,
                inputLimit: 300,
                code: "F.c.5.a",
                label: "(iii) Residency/Fellowship:",
                hidden: true,
                // label: "List special requirements and license certifications here.",
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecReqResFellow",
                  is: null,
                },
                isVisible: {
                  when: "isAltSpecReqResFellow",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isAltSpecReqResFellow",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
              {
                machine_name: "isAltSpecReqOther",
                widget: "checkbox",
                choice: "(iv) Other Special Skills or Requirements",
                label: "(iv) Other Special Skills or Requirements",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecialReq",
                  is: [0, null],
                },
                clearSchema: ["altSpecReqOther"],
              },
              {
                machine_name: "altSpecReqOther",
                widget: "text_area",
                width: 8,
                rows: 10,
                inputLimit: 4000,
                code: "F.c.5.a",
                label: "(iv) Other Special Skills or Requirements:",
                hidden: true,
                disabled: true,
                disableWhenField: {
                  when: "isAltSpecReqOther",
                  is: null,
                },
                isVisible: {
                  when: "isAltSpecReqOther",
                  is: 1,
                },
                validate: [
                  {
                    isRequired: "isAltSpecReqOther",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      valueOf: 1,
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  ],
};
